/**
 * Shooter Tank — Precision over power. Fast shots, tight craters, star-shaped chaos.
 * Shot 1: Star Burst — machine gun spray of 15 tiny star bullets.
 * Shot 2: Falling Star — tank becomes the projectile. Height-based damage scaling.
 */

import type { DeferredSpawn } from '../types';
import { registerWeapon, lookupWeapon, createProjectile } from '../weapons';
import { random } from '../terrain/rng';
import { clearCircle, applyScorch } from '../terrain/grid';
import { getCellAt, getGroundYBelow } from '../terrain/grid-core';
import { PHYSICS, KNOCKBACK, WORLD, CELL } from '../constants';
import { mirrorDIFreeze } from '../physics/transitions';
import { cancelGrapple } from '../game/grapple';
import { recordTrickHit } from '../game/trick-score';
import { registerTank } from './registry';
import { SMALL_CRATER } from '../terrain/crater-shapes';

// ─── Sub-weapon — tiny star bullet for Star Burst's spray ─────────────────

const SHOOTER_BULLET = registerWeapon({
  id: 'shooter_bullet',
  name: 'Star Bullet',
  description: '',
  tier: 'SUB',
  rarity: 'common',
  projectileRadius: 4,
  projectileSpeed: 0.65,
  color: 0xccddff,
  gravity: 0.14,
  bounces: 0,
  bounceEnergyRetention: 0.85,
  damage: 2,
  blastRadius: 18,
  knockbackMult: 0.03,
  knockbackRadius: 1.0,
  shakeIntensity: 6.0,
  particleScale: 0.3,
  craterShape: SMALL_CRATER,
  knockbackStacking: { windowFrames: 30, diminishingFactor: 0.7 },
  projectileShape: 'star',
  // Stardust trail — twinkling particles that linger and fade
  onTick(proj, _state, fx) {
    proj.behaviorState.t = (proj.behaviorState.t || 0) + 1;
    // Primary stardust — bright sparkle every 2 frames
    if (proj.behaviorState.t % 2 === 0) {
      fx.push({ type: 'particles', method: 'spawnParticle', args: [
        proj.x + (Math.random() - 0.5) * 6,
        proj.y + (Math.random() - 0.5) * 6,
        0xccddff,
        0.08, 0.4, 0.02, 0.04, 0.06, 0.12, 0, 0.93, true, false, 0.5,
      ] });
    }
    // Secondary stardust — dimmer, drifting sparkle every 4 frames
    if (proj.behaviorState.t % 4 === 0) {
      fx.push({ type: 'particles', method: 'spawnParticle', args: [
        proj.x + (Math.random() - 0.5) * 10,
        proj.y + (Math.random() - 0.5) * 10,
        0x8899cc,
        0.05, 0.25, 0.03, 0.06, 0.10, 0.18, 0, 0.96, true, false, 0.3,
      ] });
    }
    return null;
  },
});

// ─── Shot 1: Star Burst — carrier that bursts into 15 staggered star bullets

const SHOOTER_SHOT1 = registerWeapon({
  id: 'shooter_shot1',
  name: 'Star Burst',
  description: 'Machine gun spray. 15 tiny star bullets in a staggered burst.',
  tier: 'CHEAP',
  rarity: 'common',
  projectileRadius: 6,
  projectileSpeed: 0.65,
  color: 0xccddff,
  gravity: 0.14,
  bounces: 0,
  bounceEnergyRetention: 0.85,
  damage: 0,               // delivery vehicle
  blastRadius: 15,          // tiny
  knockbackMult: 0.01,
  knockbackRadius: 0.5,
  shakeIntensity: 6.0,
  particleScale: 0.2,
  craterShape: SMALL_CRATER,
  tracerMode: 'cone',
  tracerConeConfig: { subWeaponId: 'shooter_bullet', count: 7, spread: 0.125 },
  edgeguard: { chargeFrames: 20, threshold: 25 },
  onTick(proj, state, fx) {
    // On first frame, consume carrier and spawn 15 staggered bullets
    if (!proj.behaviorState.fired) {
      proj.behaviorState.fired = 1;
      const bulletWeapon = lookupWeapon('shooter_bullet');
      const baseAngle = Math.atan2(proj.vy, proj.vx);
      const speed = Math.sqrt(proj.vx * proj.vx + proj.vy * proj.vy);
      const spawns: DeferredSpawn[] = [];

      // Muzzle flash VFX
      fx.push({ type: 'particles', method: 'spawnExplosion', args: [proj.x, proj.y, 0xccddff, 8] });

      for (let i = 0; i < 15; i++) {
        // Slight random spread
        const angle = baseAngle + (random() - 0.5) * 0.25;
        // Speed varies slightly
        const bulletSpeed = speed * (0.85 + random() * 0.3);
        spawns.push({
          spawnFrame: state.frameCount + i * 2, // one bullet every 2 frames
          spawned: false,
          projectile: createProjectile(
            bulletWeapon, proj.x, proj.y,
            Math.cos(angle) * bulletSpeed,
            Math.sin(angle) * bulletSpeed,
            proj.owner, state.frameCount, proj.generation + 1,
          ),
        });
      }
      return { consume: true, spawns };
    }
    return null;
  },
});

// ─── Shot 2: Falling Star — tank becomes the projectile, height-based scaling ─

const PHASE_STARTUP = 0;
const PHASE_FALLING = 1;

const SHOOTER_SHOT2 = registerWeapon({
  id: 'shooter_shot2',
  name: 'Falling Star',
  description: 'Become the meteor. Jump high, fire, plummet. Fall distance scales everything.',
  tier: 'UNCOMMON',
  rarity: 'uncommon',
  projectileRadius: 3,
  projectileSpeed: 0.01,     // pseudo-projectile (Duelist pattern)
  color: 0xffd700,
  gravity: 0,
  bounces: 0,
  bounceEnergyRetention: 0,
  damage: 20,                // base damage (ground-level)
  blastRadius: 100,
  knockbackMult: 0.08,
  knockbackRadius: 1.2,
  shakeIntensity: 3.0,
  particleScale: 1.0,
  noCrater: true,            // crater created manually on impact
  craterShape: SMALL_CRATER,
  tracerMode: 'meteor',
  onTick(proj, state, fx) {
    proj.ignoreTerrain = true;
    proj.ignoreTanks = true;
    proj.ignoreWind = true;

    const owner = state.players[proj.owner];
    if (!owner) return 'consume';

    const bs = proj.behaviorState;

    // ── First frame: initialize ──
    if (bs.initialized === undefined) {
      bs.initialized = 1;
      bs.phase = PHASE_STARTUP;
      bs.frame = 0;
      bs.fireY = owner.y;
      bs.aimAngle = -owner.angle; // screen-space angle

      // Per-projectile weapon copy for stat mutation
      proj.weapon = { ...proj.weapon };

      // Lock tank, set dash immunity
      owner.dashImmune = 1;

      // Brief convergent energy VFX
      fx.push({ type: 'particles', method: 'spawnExplosion', args: [owner.x, owner.y, 0xffd700, 6] });
      fx.push({ type: 'particles', method: 'spawnShockwave', args: [owner.x, owner.y, 0xffd700, 20, 120, 2, 0.08] });
    }

    const phase = bs.phase;

    // ══════════════════════════════════════════════════════
    // Phase 0: STARTUP (3 frames)
    // ══════════════════════════════════════════════════════
    if (phase === PHASE_STARTUP) {
      // Lock owner in place
      owner.x = owner.x;
      owner.y = owner.y;
      owner.vx = 0;
      owner.vy = 0;
      owner.settled = false;
      proj.x = owner.x;
      proj.y = owner.y;

      bs.frame++;
      if (bs.frame >= 3) {
        bs.phase = PHASE_FALLING;
        bs.frame = 0;
        // Initialize velocity based on aim angle
        const chargeSpeed = 8;
        const aimAngle = bs.aimAngle;
        const rawVx = Math.cos(aimAngle) * chargeSpeed;
        const rawVy = Math.sin(aimAngle) * chargeSpeed;
        // Clamp upward component to 0 — can only aim horizontal or downward
        bs.fallVx = rawVx;
        bs.fallVy = Math.max(0, rawVy);
        fx.push({ type: 'screen_shake', intensity: 3, decay: 0.92 });
      }
      return null;
    }

    // ══════════════════════════════════════════════════════
    // Phase 1: FALLING
    // ══════════════════════════════════════════════════════
    if (phase === PHASE_FALLING) {
      // Apply heavy gravity to internal velocity (Duelist pattern — avoids double-movement from physics loop)
      bs.fallVy += 0.35;

      // Compute new position and assign directly
      const newX = owner.x + bs.fallVx;
      const newY = owner.y + bs.fallVy;
      owner.x = Math.max(WORLD.WORLD_LEFT + PHYSICS.ECB_RADIUS, Math.min(WORLD.WORLD_RIGHT - PHYSICS.ECB_RADIUS, newX));
      owner.y = newY;

      // Zero owner velocity — physics loop has nothing to integrate
      owner.vx = 0;
      owner.vy = 0;
      owner.settled = false;

      // Pass through wireframe platforms during fall
      owner.platformDropTimer = 2;

      // Sync projectile to owner (camera follows)
      proj.x = owner.x;
      proj.y = owner.y;

      // Calculate current fall distance for VFX scaling
      const currentFallDist = Math.max(0, owner.y - bs.fireY);
      const currentT = Math.min(currentFallDist / 800, 1);

      // Flaming falling star trail — embers, glow, speed lines (visual-only, uses Math.random)
      if (bs.frame % 2 === 0) {
        fx.push({ type: 'particles', method: 'spawnFallingStarTrail', args: [
          owner.x, owner.y, currentT, bs.fallVy,
        ] });
      }

      bs.frame++;

      // Terrain check: has tank hit sand? (wireframe ignored via platformDropTimer)
      const footY = owner.y + PHYSICS.ECB_RADIUS;
      const cellAtFoot = getCellAt(owner.x, footY);
      if (cellAtFoot === CELL.SAND) {
        const groundY = getGroundYBelow(owner.x, owner.y);
        // ── IMPACT ──
        const fallDist = Math.max(0, owner.y - bs.fireY);
        const t = Math.min(fallDist / 800, 1);

        // Scale stats
        const scaledDamage = Math.round(20 + t * 45);       // 20 → 65
        const scaledBlast = Math.round(100 + t * 80);        // 100 → 180
        const scaledKB = 0.08 + t * 0.08;                    // 0.08 → 0.16
        const scaledShake = 3.0 - t * 1.5;                   // 3.0 → 1.5
        const scaledCrater = Math.round(40 + t * 50);        // 40 → 90

        // Place tank at ground surface
        owner.y = groundY - PHYSICS.ECB_RADIUS;
        owner.vx = 0;
        owner.vy = 0;
        owner.settled = false;
        owner.dashImmune = 0;
        owner.platformDropTimer = 0;

        const impactX = owner.x;
        const impactY = groundY;

        // Carve crater
        const carveBounds = clearCircle(impactX, impactY, scaledCrater);
        if (carveBounds) {
          applyScorch(impactX, impactY, scaledCrater);
          fx.push({ type: 'terrain_update', bounds: carveBounds });
          fx.push({ type: 'particles', method: 'spawnTerrainDebris', args: [impactX, impactY, scaledCrater, 0.5 + t * 0.5] });
        }

        // Meteor impact detonation — multi-phase VFX via dedicated function
        fx.push({ type: 'particles', method: 'spawnFallingStarImpact', args: [impactX, impactY, t] });
        fx.push({ type: 'screen_shake', intensity: Math.round(20 / scaledShake), decay: 0.80 });
        if (t > 0.3) {
          fx.push({ type: 'screen_multi_flash', color: 0xffd700, alpha: 0.15 + t * 0.15 });
        }

        // Manual damage application (iterate players, check distance vs blastRadius)
        for (let i = 0; i < state.players.length; i++) {
          if (i === proj.owner) continue;
          const p = state.players[i];
          if (!p.alive) continue;

          const dx = p.x - impactX;
          const dy = p.y - impactY;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist > scaledBlast) continue;

          // Damage falloff: full damage at center, 30% at edge
          const falloff = 1 - (dist / scaledBlast) * 0.7;
          const hitDamage = Math.round(scaledDamage * falloff);
          p.damagePercent += hitDamage;

          // Match stat tracking
          if (proj.owner >= 0 && state.matchStats) {
            state.matchStats.damageDealt[proj.owner] += hitDamage;
            state.matchStats.damageTaken[i] += hitDamage;
            if (hitDamage > state.matchStats.bestSingleHit[proj.owner]) {
              state.matchStats.bestSingleHit[proj.owner] = hitDamage;
            }
            p.lastHitBy = proj.owner;
          }

          // Radial knockback from impact point
          if (state.grapple.active?.playerIndex === i) cancelGrapple(state);
          const percentMult = 1 + p.damagePercent / KNOCKBACK.KBG;
          const kbForce = scaledBlast * scaledKB * percentMult;
          const kbDist = Math.max(dist, 1);
          p.diPendingVx = (dx / kbDist) * kbForce;
          p.diPendingVy = (dy / kbDist) * kbForce;
          mirrorDIFreeze(p);
          p.settled = false;
          state.firingPhaseKnockedPlayers.push(i);

          // Hit VFX
          fx.push({ type: 'tank_flash', playerIndex: i, bright: 0xffd700, dark: 0x332200, duration: 0.3 + t * 0.2 });
          fx.push({ type: 'particles', method: 'spawnHitCross', args: [p.x, p.y, 30 + t * 20] });

          // Trick recording
          const isDirect = dist < scaledBlast * 0.3;
          recordTrickHit(state, proj, i, p.x, p.y, hitDamage, isDirect, fx);
        }

        // Announce on big hits
        if (t > 0.6) {
          fx.push({ type: 'announce_world', text: 'METEOR!', x: impactX, y: impactY - 60, color: 0xffd700, fontSize: 36, hold: 0.8 });
        }

        return 'consume';
      }

      // Fall off bottom of world — consume without impact
      if (owner.y > WORLD.VIRTUAL_HEIGHT + 200) {
        owner.dashImmune = 0;
        owner.settled = false;
        return 'consume';
      }

      return null;
    }

    // Fallback
    return 'consume';
  },
});

registerTank({
  id: 'shooter',
  name: 'Shooter',
  description: 'Precision over power. Fast shots, tight craters, star-shaped chaos.',
  visual: { bodyColor: 0xAABBCC, glowColor: 0xCCDDFF, bodyRadius: 33 },
  ecbRadius: 26,
  bonus: true,
  shots: [
    { tier: 1, weapon: SHOOTER_SHOT1, momentumCost: 0, hitlagBonus: 0, label: 'Star Burst', tierColor: 0xccddff },
    { tier: 2, weapon: SHOOTER_SHOT2, momentumCost: 0, hitlagBonus: 0, label: 'Falling Star', tierColor: 0xffd700 },
  ],
});

// Suppress unused variable warning — registered by side-effect
void SHOOTER_BULLET;
